import { Module, GetterTree, ActionTree, MutationTree } from 'vuex';
import http from '@/http';
import axios from '@/http/axios';
import Vue from 'vue';
import _, { cloneDeep } from 'lodash';
import router from '@/router';
import dayjs from '@/utils/dayjs';

const state: AuthState = {
  user: null,
  userSignature: {
    signature: '',
  },
  candidate: null,
  roles: {},
  activeRoleKey: '',
  activeKeys: [],
  rolesHasKeys: {},
  keys: [],
  businessesHasKeys: {},
  bearerAccessToken: window.localStorage.getItem('access_token') || '',
  bearerRefreshToken: window.localStorage.getItem('refresh_token') || '',
  bearerRefreshTimer: -1,
  activationStatus: '',
  activeBusiness: {},
  loginDetails: {
    email: '',
    password: '',
    totp: '',
    remember: false,
  },
  businessSignUpDetails: {
    email: '',
    first_name: '',
    last_name: '',
    position_title: '',
    business_name: '',
    mobile: '',
    password: '',
    password_confirmation: '',
  },
  candidateForgotPasswordDetails: {
    first_name: '',
    last_name: '',
    date_of_birth: dayjs().format('YYYY-MM-DD HH:mm:ss'),
    postcode: '',
    email: '',
    mobile: '',
  },
  accountResetPasswordDetails: {
    email: '',
  },
  twoFactorDetails: {
    email: '',
    totp: '',
  },
  resetPasswordDetails: {
    reset_password_token: '',
    password: '',
    password_confirmation: '',
    auto_login: false,
  },
  businesses: [],
  isImpersonate: window.localStorage.getItem('before_impersonate_access_token')
    ? true
    : false,
};

const getters: GetterTree<AuthState, RootState> = {
  bearerRefreshToken: (state) => {
    return state.bearerRefreshToken;
  },
  bearerAccessToken: (state) => {
    return state.bearerAccessToken;
  },
  userRole: (state) => {
    return state.activeRoleKey ? state.roles[state.activeRoleKey] : '';
  },
  userPreferences: (state) => {
    if (!_.isArray(state.user?.user_preferences)) {
      return {};
    }
    const userPreference = state.user?.user_preferences.find(
      (preference) => preference.category === state.activeRoleKey
    );
    if (!userPreference) {
      return {};
    }

    return userPreference.value;
  },
  hasValidatedCreditLimit: (state) => {
    return (
      state.activeBusiness.business_credit_level &&
      state.activeBusiness.business_credit_level.amount_limit > 0
    );
  },
  business_credit_level: (state) => {
    return (
      state.activeBusiness.business_credit_level ?? {
        name: 'Tier 1',
        level: 1,
        description: 'Pay As You Go Clients',
        amount_limit: 0,
      }
    );
  },
  activeRoleDateFormat: (state) => {
    if (state.activeRoleKey === 'rBusiness') {
      const dateFormat = state.activeBusiness?.date_format;
      return dateFormat ?? null;
    }

    if (state.activeRoleKey === 'rCandidate') {
      const dateFormat = state.candidate?.date_format;
      return dateFormat ?? null;
    }

    return null;
  },
  activeRoleDateTimeFormat: (state) => {
    if (state.activeRoleKey === 'rBusiness') {
      const dateFormat = state.activeBusiness?.date_format;
      const timeFormat = state.activeBusiness?.time_format;
      return dateFormat && timeFormat ? dateFormat + ' ' + timeFormat : null;
    }

    if (state.activeRoleKey === 'rCandidate') {
      const dateFormat = state.candidate?.date_format;
      const timeFormat = state.candidate?.time_format;
      return dateFormat && timeFormat ? dateFormat + ' ' + timeFormat : null;
    }

    return null;
  },
};

const actions: ActionTree<AuthState, RootState> = {
  login({ state, commit, dispatch }) {
    return http.v1.auth
      .login(state.loginDetails)
      .then((response) => {
        if (response.data.code == 'auth.require-totp') {
          // If require security code
          return {
            message: 'Require Security Code',
            status: true,
            requireTotp: true,
          };
        } else if (response.data.data) {
          // If there's user data in response

          // Set bearer token
          commit('SET_BEARER_TOKEN', response.data.data);

          dispatch('autoRefreshBearerToken');

          return {
            message: 'Login succeed!',
            status: true,
            requireTotp: false,
          };
        } else {
          return { message: 'Wrong Email or Password', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  adminAttemptLogin({ state, commit, dispatch }) {
    return http.v1.auth
      .adminAttemptLogin(state.loginDetails)
      .then((response) => {
        if (response.data.code == 'auth.require-totp') {
          // If require security code
          return {
            message: 'Require Security Code',
            status: true,
            requireTotp: true,
          };
        } else if (response.data.data) {
          // If there's user data in response
          // Set bearer token
          commit('SET_BEARER_TOKEN', response.data.data);

          localStorage.setItem('active_role_key', 'rAdmin');

          dispatch('autoRefreshBearerToken');

          return {
            message: 'Login succeed!',
            status: true,
            requireTotp: false,
          };
        } else {
          return { message: 'Wrong Email or Password', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  businessAttemptLogin({ state, commit, dispatch }) {
    return http.v1.auth
      .businessAttemptLogin(state.loginDetails)
      .then((response) => {
        if (response.data.code == 'auth.require-totp') {
          // If require security code
          return {
            message: 'Require Security Code',
            status: true,
            requireTotp: true,
          };
        } else if (response.data.data) {
          // If there's user data in response

          // Set bearer token
          commit('SET_BEARER_TOKEN', response.data.data);

          localStorage.setItem('active_role_key', 'rBusiness');

          dispatch('autoRefreshBearerToken');

          return {
            message: 'Login succeed!',
            status: true,
            requireTotp: false,
          };
        } else {
          return {
            message: 'Wrong Email or Password',
            status: false,
            requireTotp: false,
          };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  sendActivationEmail(context, email) {
    return http.v1.auth.sendActivationEmail(email);
  },
  businessSignUp({ state }) {
    return http.v1.auth.businessSignUp(state.businessSignUpDetails);
  },
  businessActivate(context, code) {
    return http.v1.auth.businessActivate(code);
  },
  candidateAttemptLogin({ state, commit, dispatch }) {
    return http.v1.auth
      .candidateAttemptLogin(state.loginDetails)
      .then((response) => {
        if (response.data.code == 'auth.require-totp') {
          // If require security code
          return {
            message: 'Require Security Code',
            status: true,
            requireTotp: true,
          };
        } else if (response.data.data) {
          // If there's user data in response

          // Set bearer token
          commit('SET_BEARER_TOKEN', response.data.data);

          localStorage.setItem('active_role_key', 'rCandidate');

          dispatch('autoRefreshBearerToken');

          return {
            message: 'Login succeed!',
            status: true,
            requireTotp: false,
          };
        } else {
          return { message: 'Wrong Email or Password', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  candidateForgotPasswordPreRegister({ state, commit }) {
    return http.v1.auth
      .candidateForgotPasswordPreRegister(state.candidateForgotPasswordDetails)
      .then((response) => {
        if (response.data.data) {
          commit('SET_2FA_DETAILS', {
            email: state.candidateForgotPasswordDetails.email,
          });
          return { message: 'Account details matched!', status: true };
        } else {
          return { message: 'Wrong details', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  candidateResetPassword({ state, commit }) {
    return http.v1.auth
      .candidateResetPassword(state.candidateForgotPasswordDetails)
      .then((response) => {
        if (response.data.data) {
          commit('SET_2FA_DETAILS', {
            email: state.candidateForgotPasswordDetails.email,
          });
          return { message: 'Account details matched!', status: true };
        } else {
          return { message: 'Wrong details', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  candidateForgotPassword2fa({ state, commit }) {
    return http.v1.auth
      .candidateForgotPassword2fa(state.twoFactorDetails)
      .then((response) => {
        commit('SET_RESET_PASSWORD_DETAILS', {
          reset_password_token: response.data.data.reset_password_token,
        });
      });
  },
  candidateForgotPasswordResetPassword({ state, commit, dispatch }) {
    return http.v1.auth
      .candidateForgotPasswordResetPassword(state.resetPasswordDetails)
      .then((response) => {
        if (state.resetPasswordDetails.auto_login) {
          // Set bearer token
          commit('SET_BEARER_TOKEN', response.data.data);

          dispatch('autoRefreshBearerToken');

          return {
            message: 'Login succeed!',
            status: true,
            requireTotp: false,
          };
        }
      });
  },
  businessResetPassword({ state, commit }) {
    return http.v1.auth
      .businessResetPassword(state.accountResetPasswordDetails)
      .then((response) => {
        if (response.data.data) {
          commit('SET_2FA_DETAILS', {
            email: state.accountResetPasswordDetails.email,
          });
          return { message: 'Account details matched!', status: true };
        } else {
          return { message: 'Wrong details', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  businessResetPassword2fa({ state, commit }) {
    return http.v1.auth
      .businessResetPassword2fa(state.twoFactorDetails)
      .then((response) => {
        commit('SET_RESET_PASSWORD_DETAILS', {
          reset_password_token: response.data.data.reset_password_token,
        });
      });
  },
  businessResetPasswordResetPassword({ state }) {
    return http.v1.auth.businessResetPasswordResetPassword(
      state.resetPasswordDetails
    );
  },
  adminResetPassword({ state, commit }) {
    return http.v1.auth
      .adminResetPassword(state.accountResetPasswordDetails)
      .then((response) => {
        if (response.data.data) {
          commit('SET_2FA_DETAILS', {
            email: state.accountResetPasswordDetails.email,
          });
          return { message: 'Account details matched!', status: true };
        } else {
          return { message: 'Wrong details', status: false };
        }
      })
      .catch(({ response }) => {
        let errorMessage = response.data.error;
        if (
          typeof errorMessage === 'string' ||
          errorMessage instanceof String
        ) {
          errorMessage = [[errorMessage]];
        }
        return {
          message: errorMessage,
          status: false,
        };
      });
  },
  adminResetPassword2fa({ state, commit }) {
    return http.v1.auth
      .adminResetPassword2fa(state.twoFactorDetails)
      .then((response) => {
        commit('SET_RESET_PASSWORD_DETAILS', {
          reset_password_token: response.data.data.reset_password_token,
        });
      });
  },
  adminResetPasswordResetPassword({ state }) {
    return http.v1.auth.adminResetPasswordResetPassword(
      state.resetPasswordDetails
    );
  },
  fetchUser({ commit, getters, dispatch }) {
    console.debug('fetchUser.start');

    const accessToken = getters.bearerAccessToken;

    if (!accessToken) {
      console.debug('accessToken is none');
      return false;
    } else {
      // First time load, force SET_BEARER_ACCESS_TOKEN and set bearer auto refresh
      console.debug('reset bearer access token');
      commit('SET_BEARER_ACCESS_TOKEN', accessToken);
      dispatch('autoRefreshBearerToken');
    }

    return http.v1.auth
      .details()
      .then((response) => {
        // If there's user data in response
        if (response.data.data) {
          // Update user details
          const payload = response.data.data;
          commit('SET_USER_INFO', payload);

          let activeRoleKey = localStorage.getItem('active_role_key');
          const { roles } = response.data.data;
          if (!activeRoleKey || !Object.keys(roles).includes(activeRoleKey)) {
            activeRoleKey =
              Object.keys(roles).length === 1
                ? Object.keys(roles)[0]
                : 'rNoActiveRole';
          }
          commit('SET_ACTIVE_ROLE', activeRoleKey);

          if (activeRoleKey === 'rBusiness') {
            const activeBusiness =
              localStorage.getItem('active_business') || null;
            commit('SET_ACTIVE_BUSINESS', activeBusiness);

            // store timezone
            const business = payload.businesses.find(
              (bus) => bus.object_id === activeBusiness
            );
            if (business?.timezone) {
              localStorage.setItem('que_timezone', business.timezone);
            } else {
              localStorage.removeItem('que_timezone');
            }

            if ('user_preference' in business) {
              commit(
                'UserPreference/SET_STATE',
                {
                  key: 'single',
                  payload: cloneDeep(business.user_preference),
                },
                { root: true }
              );
            }
          }
          if (activeRoleKey === 'rCandidate') {
            localStorage.setItem('que_timezone', payload.candidate.timezone);
          }

          return true;
        } else {
          return false;
        }
      })
      .catch(() => {
        return false;
      });
  },
  autoRefreshBearerToken({ commit, dispatch, getters }) {
    const refreshToken = getters.bearerRefreshToken;
    const expiresAt = localStorage.getItem('expires_at');

    if (expiresAt && refreshToken) {
      const now = Date.now() / 1000;
      let timeUntilRefresh = parseInt(expiresAt) - Math.round(now);
      timeUntilRefresh *= 0.75;
      const timer = setTimeout(
        () => dispatch('refreshBearerToken'),
        timeUntilRefresh * 1000
      );
      commit('SET_BEARER_REFRESH_TIMER', timer);
    }
  },
  logout({ commit }, activeRoleKey) {
    return http.v1.auth.logout().then(() => {
      commit('CLEAR_STATE');

      let routeName = 'login';
      switch (activeRoleKey) {
        case 'rAdmin':
          routeName = 'administrator-login';
          break;
        case 'rBusiness':
          if (process.env.VUE_APP_WP_VERSION == 'v2') {
            routeName = 'v2-business-login';
          } else {
            routeName = 'business-login';
          }
          break;
        case 'rCandidate':
          if (process.env.VUE_APP_WP_VERSION == 'v2') {
            routeName = 'v2-candidate-login';
          } else {
            routeName = 'candidate-login';
          }
          break;
        default:
          break;
      }

      router.push({
        name: routeName,
      });
    });
  },
  updateUser({ commit }, payload) {
    console.debug('updateUser.start');
    return http.v1.auth.update(payload).then((response) => {
      // If there's user data in response
      if (response.data.data) {
        // Update user details
        const payload = response.data.data;
        commit('SET_USER', payload);
      }
      return response;
    });
  },
  changePassword(_, payload) {
    console.debug('changePassword.start');

    return http.v1.auth.changePassword(payload);
  },
  async impersonateUser({ commit, dispatch }, accessToken) {
    await commit('SET_IMPERSONATE_INFO', accessToken);
    dispatch('fetchUser').then(() => {
      router.replace({ name: 'dashboard' });
    });
  },
  async stopImpersonate({ commit, dispatch }) {
    await commit('STOP_IMPERSONATE');
    dispatch('autoRefreshBearerToken').then(() => {
      dispatch('fetchUser');
    });
  },
  signature({ commit }, payload) {
    return http.v1.auth.signature(payload).then((response) => {
      // If there's user data in response
      if (response.data.data) {
        // Update user details
        const payload = response.data.data;
        commit('SET_USER_SIGNATURE', payload);
      }
      return response;
    });
  },
  getSignature({ commit }) {
    return http.v1.auth.getSignature().then((response) => {
      // If there's user data in response
      if (response.data.data) {
        // Update user details
        const payload = response.data.data;
        commit('SET_USER_SIGNATURE', payload);
      }
      return response;
    });
  },
};

const mutations: MutationTree<AuthState> = {
  SET_USER_INFO(state, payload) {
    const {
      user,
      roles,
      keys,
      businessesHasKeys,
      rolesHasKeys,
      businesses,
      candidate,
    } = payload;
    state.user = user;
    state.roles = roles;
    state.rolesHasKeys = rolesHasKeys;
    state.keys = keys;
    state.businessesHasKeys = businessesHasKeys;
    state.businesses = businesses;
    state.candidate = candidate;
  },
  SET_USER(state, user) {
    state.user = user;
  },
  SET_CANDIDATE(state, candidate) {
    state.candidate = candidate;
  },
  SET_USER_SIGNATURE(state, signature) {
    state.userSignature = signature;
  },
  SET_ACTIVE_ROLE(state, roleKey = 'rNoActiveRole') {
    state.activeRoleKey = roleKey;
    localStorage.setItem('active_role_key', roleKey);
    if (roleKey === 'rNoActiveRole') {
      state.activeKeys = [roleKey];
    } else if (state.rolesHasKeys) {
      state.activeKeys = [state.activeRoleKey].concat(
        state.rolesHasKeys[state.activeRoleKey]
      );
    } else {
      // To handle landing page redirect
      // Set active role when rolesHasKeys is null
      state.activeKeys = [roleKey];
    }
  },
  SET_ACTIVE_BUSINESS(state, businessOid = null) {
    if (state.businesses.length > 0) {
      if (businessOid) {
        state.activeBusiness = state.businesses.find(
          (business) => business.object_id === businessOid
        );
      }

      if (!businessOid || !state.activeBusiness) {
        state.activeBusiness = state.businesses[0];
      }
      localStorage.setItem('active_business', state.activeBusiness.object_id);
    }
  },
  SET_BEARER_TOKEN(state, payload) {
    const { access_token, refresh_token, expires_in } = payload;

    const now = Date.now() / 1000;
    state.bearerAccessToken = access_token;
    state.bearerRefreshToken = refresh_token;
    http.v1.initBearerToken();
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('refresh_token', refresh_token);
    localStorage.setItem(
      'expires_at',
      Math.round(now) + Number(expires_in).toString()
    );
  },
  SET_BEARER_ACCESS_TOKEN(state, accessToken) {
    state.bearerAccessToken = accessToken;
    localStorage.setItem('access_token', accessToken);
    http.v1.initBearerToken();
  },
  SET_BEARER_REFRESH_TOKEN(state, refreshToken) {
    axios.defaults.headers.common['Authorization'] = '';
    state.bearerAccessToken = '';
    state.bearerRefreshToken = refreshToken;
    localStorage.removeItem('access_token');
    localStorage.setItem('refresh_token', refreshToken);
    localStorage.removeItem('expires_at');
  },
  SET_BEARER_REFRESH_TIMER(state, timer) {
    state.bearerRefreshTimer = timer;
  },
  SET_ACTIVATION_STATUS(state, activationStatus) {
    state.activationStatus = activationStatus;
  },
  CLEAR_GUEST_STATE(state) {
    state.loginDetails = {
      email: '',
      password: '',
      totp: '',
      remember: false,
    };
    state.businessSignUpDetails = {
      email: '',
      first_name: '',
      last_name: '',
      position_title: '',
      business_name: '',
      mobile: '',
      password: '',
      password_confirmation: '',
    };
    state.candidateForgotPasswordDetails = {
      first_name: '',
      last_name: '',
      date_of_birth: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      postcode: '',
      email: '',
      mobile: '',
    };
    state.accountResetPasswordDetails = {
      email: '',
    };
    state.twoFactorDetails = {
      email: '',
      totp: '',
    };
    state.resetPasswordDetails = {
      reset_password_token: '',
      password: '',
      password_confirmation: '',
    };
  },
  CLEAR_STATE(state) {
    state.user = null;
    state.roles = {};
    state.activeRoleKey = '';
    state.activeKeys = [];
    state.keys = [];
    state.businessesHasKeys = {};
    state.bearerAccessToken = '';
    state.bearerRefreshToken = '';
    state.bearerRefreshTimer = -1;
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('hide_alert');
  },
  UNSET_ACTIVE_ROLE_KEY() {
    localStorage.removeItem('active_role_key');
  },
  UNSET_BEARER_REFRESH_TOKEN(state) {
    state.bearerRefreshToken = '';
    localStorage.removeItem('refresh_token');
  },
  UPDATE_PREFERENCES(state, payload) {
    const index = state.user?.user_preferences.findIndex(
      (preference) => preference.id === payload.id
    );
    if (index >= 0) {
      Vue.set(state.user?.user_preferences, index, payload);
    } else {
      state.user?.user_preferences.push(payload);
    }
    return payload;
  },
  SET_LOGIN_DETAILS(state, payload) {
    this.loginDetails = Object.assign(state.loginDetails, payload);
  },
  SET_2FA_DETAILS(state, payload) {
    this.twoFactorDetails = Object.assign(state.twoFactorDetails, payload);
  },
  SET_RESET_PASSWORD_DETAILS(state, payload) {
    this.resetPasswordDetails = Object.assign(
      state.resetPasswordDetails,
      payload
    );
  },
  SET_IMPERSONATE_INFO(state, accessToken) {
    state.bearerAccessToken = accessToken;
    state.bearerRefreshToken = '';
    state.bearerRefreshTimer = -1;

    const before_impersonate_access_token =
      localStorage.getItem('access_token') || '';
    const before_impersonate_refresh_token =
      localStorage.getItem('refresh_token') || '';
    const before_impersonate_expires_at =
      localStorage.getItem('expires_at') || '';

    localStorage.setItem('access_token', accessToken);
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('active_role_key');

    localStorage.setItem(
      'before_impersonate_access_token',
      before_impersonate_access_token
    );
    localStorage.setItem(
      'before_impersonate_refresh_token',
      before_impersonate_refresh_token
    );
    localStorage.setItem(
      'before_impersonate_expires_at',
      before_impersonate_expires_at
    );
    localStorage.setItem(
      'before_impersonate_active_role_key',
      state.activeRoleKey
    );

    state.activeRoleKey = '';
    state.isImpersonate = true;
  },
  STOP_IMPERSONATE(state) {
    state.bearerAccessToken =
      localStorage.getItem('before_impersonate_access_token') || '';
    state.bearerRefreshToken =
      localStorage.getItem('before_impersonate_refresh_token') || '';

    localStorage.setItem(
      'access_token',
      localStorage.getItem('before_impersonate_access_token') || ''
    );
    localStorage.setItem(
      'refresh_token',
      localStorage.getItem('before_impersonate_refresh_token') || ''
    );
    localStorage.setItem(
      'expires_at',
      localStorage.getItem('before_impersonate_expires_at') || ''
    );
    localStorage.setItem('active_role_key', 'rAdmin');

    localStorage.removeItem('before_impersonate_access_token');
    localStorage.removeItem('before_impersonate_refresh_token');
    localStorage.removeItem('before_impersonate_expires_at');
    localStorage.removeItem('before_impersonate_active_role_key');

    state.isImpersonate = false;
  },
  SET_AUTO_LOGIN(state, autoLogin) {
    state.resetPasswordDetails.auto_login = autoLogin;
  },
};

const auth: Module<AuthState, RootState> = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};

export default auth;
